import React, { useState, useCallback } from 'react';
import { Box, Grid, Paper, Typography, Alert, Snackbar } from '@mui/material';
import ChatInterface from '../../common/ChatInterface';
import StepHeader from '../../common/StepHeader';
import ProblemStatementEditor from './ProblemStatementEditor';
import {
  useWorkflow,
  type ChatHistoryMessage,
} from '../../../contexts/WorkflowContext';
import type {
  QualityReport,
  StepStructuredData,
} from '../../../services/workflow';
import { useDiscoveryChat } from './useDiscoveryChat';
import type {
  FeedbackRating,
  MessageFeedbackState,
} from '../../../types/feedback';

// Props for step data loaded from backend (for resume functionality)
interface DiscoveryStepProps {
  initialChatHistory?: ChatHistoryMessage[];
  initialSpecData?: StepStructuredData | null;
  initialQualityScore?: number;
  initialQualityReport?: QualityReport;
}

const DiscoveryStep: React.FC<DiscoveryStepProps> = ({
  initialChatHistory,
  initialSpecData,
  initialQualityScore,
  initialQualityReport,
}) => {
  const { projectId } = useWorkflow();

  // Track which messages have received feedback
  const [messageFeedback, setMessageFeedback] = useState<MessageFeedbackState>(
    {}
  );

  const handleFeedbackSubmit = useCallback(
    (messageIndex: number, rating: FeedbackRating) => {
      setMessageFeedback((prev) => ({
        ...prev,
        [messageIndex]: rating,
      }));
    },
    []
  );

  // Use custom hook for all chat/draft logic
  const {
    messages,
    isLoading,
    error,
    clearError,
    draft,
    qualityScore,
    qualityScoreLabel,
    sectionFeedback,
    qualityReport,
    clarityPercent,
    isSaving,
    sendMessage,
    saveDraft,
    askAboutSection,
    attachments,
    addFiles,
    removeFile,
    isUploading,
    uploadProgress,
    snackbarMessage,
    clearSnackbar,
  } = useDiscoveryChat({
    initialChatHistory,
    initialSpecData,
    initialQualityScore,
    initialQualityReport,
  });

  return (
    <>
      <StepHeader step="discovery" />
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Left panel - Chat */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
                {error}
              </Alert>
            )}
            <ChatInterface
              messages={messages}
              onSendMessage={sendMessage}
              isLoading={isLoading}
              height="calc(100vh - 250px)"
              enableAttachments={true}
              attachments={attachments}
              onFilesSelected={addFiles}
              onRemoveAttachment={removeFile}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
              enableFeedback={!!projectId}
              projectId={projectId || undefined}
              step="discovery"
              messageFeedback={messageFeedback}
              onFeedbackSubmit={handleFeedbackSubmit}
            />
          </Box>
        </Grid>

        {/* Right panel - Draft editor */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              height: 'calc(100vh - 250px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* Problem statement editor (includes quality display) */}
            <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
              <ProblemStatementEditor
                draft={draft}
                onSave={saveDraft}
                onAskAboutSection={askAboutSection}
                sectionFeedback={sectionFeedback}
                qualityScore={qualityScore}
                qualityScoreLabel={qualityScoreLabel}
                isLoading={isLoading}
                isSaving={isSaving}
              />
            </Box>

            {/* Quality indicator - styled based on quality score */}
            <Paper
              sx={{
                p: 2,
                flexShrink: 0,
                bgcolor:
                  clarityPercent >= 80
                    ? 'success.light'
                    : clarityPercent >= 50
                      ? 'warning.light'
                      : 'grey.100',
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {clarityPercent >= 80
                  ? 'Great job! Your problem statement is ready for the design phase.'
                  : clarityPercent >= 50
                    ? 'Your draft is taking shape. You can proceed or continue refining.'
                    : 'Keep iterating to strengthen your problem statement, or proceed when ready.'}
              </Typography>
              {qualityReport &&
                !qualityReport.can_proceed &&
                qualityReport.issues.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
                  >
                    Suggestions: {qualityReport.issues.slice(0, 2).join(', ')}
                  </Typography>
                )}
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={clearSnackbar}
        message={snackbarMessage}
      />
    </>
  );
};

export default DiscoveryStep;
